import { useMemo } from 'react';
import { toast } from 'sonner';
import { Languages } from 'lucide-react';
import { CineCombo } from './CineCombo';

const FILIPINO = /filipino|tagalog|taglish|cebuano|bisaya/i;

export const accentLabel = (catalog, id) => (catalog.scene?.accents || []).find((a) => a.id === (id || ''))?.label || '';

/** Accent dropdown shared by the production, each character and each roster actor. `inherit` adds "use the default". */
export function AccentSelect({ catalog, accents, value, onChange, inherit, testId, label = 'Accent', className }) {
  const list = accents || catalog?.scene?.accents || [];
  const byId = (id) => list.find((a) => a.id === id);
  const shown = value ? byId(value)?.label : (inherit || byId('actor')?.label);
  const options = [...(inherit ? [inherit] : []), ...list.map((a) => a.label)];
  return (
    <CineCombo label={label} className={className} value={shown || ''} options={options} placeholder={inherit || 'Choose an accent'} testId={testId} clearLabel={inherit || 'Default'}
      onChange={(lbl) => onChange(!lbl || lbl === inherit ? '' : (list.find((a) => a.label === lbl)?.id || ''))} />
  );
}

/** Step 1 · production language + default accent (characters and actors can override it in Cast & Voices). */
export function LanguageAccentCard({ project, catalog, onPatch }) {
  const languages = useMemo(() => [...new Set([...(catalog.scene?.languages || []), ...(catalog.cultures || []).map((c) => c.language)])], [catalog]);
  const accent = project.accent ?? (FILIPINO.test(project.language || '') ? 'filipino' : '');
  const eleven = (catalog.scene?.accents || []).find((a) => a.id === (accent || 'actor'))?.eleven !== false;
  const setLanguage = (language) => {
    if (!language) return;
    const patch = { language };
    if (FILIPINO.test(language) && !project.accent) { patch.accent = 'filipino'; toast.success('Filipino voices selected — every Tagalog line is voiced by a real Pinoy voice'); }
    onPatch(patch);
  };
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/40 p-5 md:col-span-2" data-testid="language-accent-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Languages size={15} className="text-amber-300" /> Language & accent</div>
      <p className="mb-4 text-xs text-neutral-400">The language every line is written and spoken in, and how the cast sounds. Any character or actor can have their own in Cast & Voices.</p>
      <div className="grid gap-4 md:grid-cols-2">
        <CineCombo label="Dialogue language" value={project.language || 'English'} options={languages} allowCustom onChange={setLanguage} testId="project-language" clearLabel="English"
          placeholder="English" />
        <AccentSelect catalog={catalog} value={accent} onChange={(v) => onPatch({ accent: v })} testId="project-accent" label="Default accent" />
      </div>
      <div className="mt-3 text-[11px] text-neutral-400" data-testid="project-voice-engine">
        {eleven ? 'Voices: each actor\'s own ElevenLabs voice speaks the line in the dialogue language (paid, inside the per-episode cap).'
          : 'Voices: real regional Microsoft voices — authentic accent and pronunciation, and free (no Kie credits).'}
      </div>
    </div>
  );
}
